import { useState, type Ref } from 'react'

import { WidgetSurface } from '@/features/card-preview/card-view'
import { AiAssistantMessageBody } from '@/features/conversation/components/conversation-message/ai-assistant-message/ai-assistant-message-body'
import { TemplateRenderer } from '@/features/conversation/components/conversation-message/ai-assistant-message/widget-template/template-renderer'
import type { WidgetTemplate } from '@/features/conversation/types/message'
import { resolveCard, type Card, type ResolveOptions } from './resolve'

/**
 * A whole reply as the widget shows it: pieces in order (text, a card, more text) and,
 * under the message, tap-to-reply suggestions or a question.
 *
 * Accepted as it arrives from any side:
 *   - Danah's final `parts` event data:  { parts: [{type:"text", text}, {type:"component", component}], next: [...] }
 *   - the widget's stored message:      { parts: [{type:"text", content}, {type:"template", widget_template}], suggestions, next_messages }
 *   - just the pieces:                  [ … ]
 * A card piece may be any card resolveCard takes (Pair's widget template, an agent component, a name with data).
 * Under the message: suggestions or a question (never both), and the agent's action buttons beside them.
 */
export type Message = Record<string, unknown> | unknown[]

export type ResolvedPart = { type: 'text'; text: string } | { type: 'card'; template: WidgetTemplate }
export interface NextOptions {
  /** tap-to-send suggestions */
  suggestions: string[]
  /** a closed question with its answers */
  question?: { text: string; options: string[]; multi: boolean }
  /** the agent's action buttons ("Add the whole routine to the cart"): a tap runs the action, it sends no message */
  actions: Array<{ id: string; label: string; data: Record<string, unknown> }>
}
export interface ResolvedMessage {
  parts: ResolvedPart[]
  next: NextOptions
  lang: 'ar' | 'en'
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))

/** The card inside a piece, whichever side wrote it; null for a text piece. */
function cardOf(p: Record<string, unknown>): Card | null {
  if (isObj(p.component)) return p.component
  if (isObj(p.widget_template)) return p.widget_template
  if (p.type === 'template' || p.type === 'component' || isObj(p.content) || typeof p.template === 'string') return p
  return null
}

function textOf(p: Record<string, unknown>): string | null {
  if (typeof p.text === 'string' && !cardOf(p)) return p.text
  if (p.type === 'text') return str(p.text ?? p.content)
  return null
}

function optionLabels(list: unknown): string[] {
  if (!Array.isArray(list)) return []
  return list.map((o) => (isObj(o) ? str(o.label ?? o.text ?? o.id) : str(o))).filter((s) => s.trim() !== '')
}

/** An action row's buttons, from Danah's `next` or the widget's message: their label in the reply's language. */
function actionItems(list: unknown, lang: 'ar' | 'en'): NextOptions['actions'] {
  if (!Array.isArray(list)) return []
  return list.filter(isObj).filter((a) => typeof a.id === 'string' && a.id).map((a) => {
    const i18n = isObj(a.label_i18n) ? a.label_i18n : {}
    const label = str(a.label).trim() || str(i18n[lang]).trim() || str(i18n.en).trim() || str(i18n.ar).trim() || (lang === 'ar' ? 'تنفيذ' : 'Go')
    return { id: str(a.id), label, data: isObj(a.data) ? a.data : {} }
  })
}

/** Suggestions, a question and action buttons, from Danah's `next` components or the widget's message fields. */
function nextOf(m: Record<string, unknown>, lang: 'ar' | 'en'): NextOptions {
  const out: NextOptions = { suggestions: [], actions: [] }
  const comps = Array.isArray(m.next) ? m.next : []
  for (const c of comps) {
    if (!isObj(c)) continue
    if (c.type === 'suggestions') out.suggestions.push(...optionLabels(c.items))
    if (c.type === 'question') out.question = { text: str(c.text), options: optionLabels(c.options), multi: c.select === 'multi' }
    if (c.type === 'actions') out.actions.push(...actionItems(c.items, lang))
  }
  const sug = m.suggestions
  if (Array.isArray(sug)) out.suggestions.push(...optionLabels(sug))
  else if (isObj(sug)) out.suggestions.push(...optionLabels(sug.options))
  for (const n of Array.isArray(m.next_messages) ? m.next_messages : []) {
    if (!isObj(n)) continue
    if (n.type === 'question') out.question = { text: str(n.question ?? n.text), options: optionLabels(n.options), multi: n.input_type === 'multi' }
    if (n.type === 'suggestions') out.suggestions.push(...optionLabels(n.options))
    if (n.type === 'actions') out.actions.push(...actionItems(n.items, lang))
  }
  // one way to answer under a message: a question with its answers wins over suggestions, never both
  if (out.question && out.question.options.length > 0) out.suggestions = []
  return out
}

/**
 * The message as its pieces, every card resolved (Pair's own cards and the commerce cards with
 * no network; any other studio template through `resolveUrl`). A card that cannot be resolved is
 * reported through `onError` and left out, so one bad card never hides the rest of the reply.
 */
export async function resolveMessage(
  message: Message,
  opts: ResolveOptions & { onError?: (e: Error) => void } = {},
): Promise<ResolvedMessage> {
  const m: Record<string, unknown> = Array.isArray(message) ? { parts: message } : isObj(message) ? message : {}
  // a single card passed where a message was expected is drawn as a one-piece message
  const raw: unknown[] = Array.isArray(m.parts) ? m.parts : cardOf(m) ? [m] : typeof m.content === 'string' ? [{ type: 'text', text: m.content }] : []
  const texts = raw.filter(isObj).map((p) => textOf(p) ?? '').join(' ')
  const lang: 'ar' | 'en' = opts.lang ?? (/[؀-ۿ]/.test(texts) ? 'ar' : 'en')
  const parts = await Promise.all(
    raw.map(async (p): Promise<ResolvedPart | null> => {
      if (!isObj(p)) return null
      const card = cardOf(p)
      if (!card) {
        const t = textOf(p)
        return t && t.trim() ? { type: 'text', text: t } : null
      }
      try {
        return { type: 'card', template: await resolveCard(card, { ...opts, lang: opts.lang ?? lang }) }
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err))
        if (opts.onError) opts.onError(e)
        else console.warn('[pair-templates] card left out:', e.message)
        return null
      }
    }),
  )
  return { parts: parts.filter((p): p is ResolvedPart => !!p), next: nextOf(m, lang), lang }
}

/**
 * What the customer did with the message: tapped a suggestion or an answer (send `text` as their message), or a
 * button that runs an action — under the message or on a card — with its data (the product it belongs to…).
 */
export type MessageAction =
  | { type: 'suggestion' | 'answer'; text: string }
  | { type: 'action'; id: string; text: string; data: Record<string, unknown> }

/** What an action did, as the page that ran it says: shown on the button (or the card's toast). */
export interface ActionResult {
  success: boolean
  message?: string
}

type OnAction = (a: MessageAction) => Promise<ActionResult | void> | ActionResult | void

/** The reply drawn the way the widget draws it, its options under it. */
export function MessageView({
  message,
  theme,
  padding,
  boxRef,
  onAction,
}: {
  message: ResolvedMessage
  theme?: 'light' | 'dark'
  padding?: number
  boxRef?: Ref<HTMLDivElement>
  onAction?: OnAction
}) {
  const dark = theme === 'dark'
  const chip = `rounded-full border px-3 py-1.5 text-[13px] leading-tight transition-colors ${
    dark ? 'border-white/20 text-white hover:bg-white/10' : 'border-[#E0E0E0] bg-white text-[#1A1A1A] hover:bg-[#F5F5F5]'
  } ${onAction ? 'cursor-pointer' : 'cursor-default'}`
  const { parts, next } = message
  const [taps, setTaps] = useState<Record<number, { busy?: boolean; ok?: boolean; message?: string }>>({})
  const ar = message.lang === 'ar'
  const runAction = async (i: number, a: NextOptions['actions'][number]) => {
    if (!onAction || taps[i]?.busy || taps[i]?.ok) return
    setTaps((t) => ({ ...t, [i]: { busy: true } }))
    try {
      const r = await onAction({ type: 'action', id: a.id, text: a.label, data: a.data })
      setTaps((t) => ({ ...t, [i]: { ok: r ? r.success : true, message: r?.message } }))
    } catch (err) {
      setTaps((t) => ({ ...t, [i]: { ok: false, message: err instanceof Error ? err.message : undefined } }))
    }
  }
  // a card's own buttons (Add to cart…) reach the page as an action too, with the product they belong to
  const onCardAction = onAction
    ? async (e: { id: string; data: Record<string, unknown> }) => {
        const r = await onAction({ type: 'action', id: e.id, text: '', data: e.data })
        return r ?? { success: true }
      }
    : undefined
  return (
    <WidgetSurface lang={message.lang} theme={theme} boxRef={boxRef} padding={padding} onAction={onCardAction}>
      <div className={`flex w-full flex-col items-start gap-4${dark ? ' pair-dark' : ''}`}>
        {parts.map((p, i) =>
          p.type === 'text' ? (
            <AiAssistantMessageBody key={i} content={p.text} />
          ) : (
            <TemplateRenderer key={i} widgetTemplate={p.template} sourceMessageUlid="preview" />
          ),
        )}
        {next.question && next.question.options.length > 0 && (
          <div className="flex flex-col gap-2" dir="auto">
            {next.question.text && <p className={`text-sm font-semibold ${dark ? 'text-white' : 'text-[#1A1A1A]'}`}>{next.question.text}</p>}
            <div className="flex flex-wrap gap-2">
              {next.question.options.map((o, i) => (
                <button key={i} type="button" className={chip} onClick={() => onAction?.({ type: 'answer', text: o })}>{o}</button>
              ))}
            </div>
          </div>
        )}
        {next.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2" dir="auto">
            {next.suggestions.map((s, i) => (
              <button key={i} type="button" className={chip} onClick={() => onAction?.({ type: 'suggestion', text: s })}>{s}</button>
            ))}
          </div>
        )}
        {next.actions.length > 0 && (
          <div className="flex flex-wrap gap-2" dir="auto">
            {next.actions.map((a, i) => {
              const st = taps[i] ?? {}
              const tone = st.ok === true ? '#15803d' : st.ok === false ? '#D92D20' : dark ? '#FFFFFF' : '#1A1A1A'
              return (
                <button
                  key={i}
                  type="button"
                  data-pair-action={a.id}
                  disabled={!onAction || st.busy || st.ok === true}
                  onClick={() => void runAction(i, a)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] leading-tight font-semibold transition-opacity ${onAction ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} disabled:cursor-default`}
                  style={{ color: tone, borderColor: tone, background: st.ok === true ? '#15803d14' : 'transparent' }}
                >
                  <svg aria-hidden width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    {st.ok === true ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <path d="M3 4h2l2.4 11.2a2 2 0 002 1.6h7.8a2 2 0 002-1.6L21 8H6" />}
                  </svg>
                  <span>{st.message && !st.busy ? st.message : st.busy ? (ar ? 'جاري التنفيذ' : 'Working') : a.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </WidgetSurface>
  )
}
